import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  UserGroupIcon,
  CubeIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    newUsers: 0,
    newProducts: 0,
    totalStock: 0,
    totalDebts: 0,
    totalExpenses: 0,
    totalSales: 0,
    prevTotalStock: 0,
    prevTotalDebts: 0,
    prevTotalExpenses: 0,
    prevTotalSales: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [customRange, setCustomRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [appliedRange, setAppliedRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [periodLabel, setPeriodLabel] = useState('');

  useEffect(() => {
    fetchStats();
    fetchRecentActivities();
  }, [period]);

  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  const toInputDate = (date) => date.toISOString().split('T')[0];

  const getRangeForPeriod = (targetPeriod) => {
    const now = new Date();
    let start = now;
    let end = now;
    let label = '';

    if (targetPeriod === 'monthly') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      label = start.toLocaleString(undefined, { month: 'long', year: 'numeric' });
    } else if (targetPeriod === 'quarterly') {
      const quarterStart = Math.floor(now.getMonth() / 3) * 3;
      start = new Date(now.getFullYear(), quarterStart, 1);
      end = new Date(now.getFullYear(), quarterStart + 3, 0);
      const quarter = Math.floor(now.getMonth() / 3) + 1;
      label = `Q${quarter} ${now.getFullYear()}`;
    } else if (targetPeriod === 'yearly') {
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now.getFullYear(), 11, 31);
      label = `${now.getFullYear()}`;
    } else {
      const customStart = customRange.startDate
        ? new Date(customRange.startDate)
        : new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
      const customEnd = customRange.endDate
        ? new Date(customRange.endDate)
        : now;
      start = customStart;
      end = customEnd;
      label = `${customStart.toLocaleDateString()} - ${customEnd.toLocaleDateString()}`;
    }

    return {
      startDate: startOfDay(start),
      endDate: endOfDay(end),
      label
    };
  };

  const getPreviousRange = (startDate, endDate) => {
    const start = startOfDay(startDate);
    const end = endOfDay(endDate);
    const durationMs = end.getTime() - start.getTime();
    const prevEnd = new Date(start.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - durationMs);
    return {
      startDate: startOfDay(prevStart),
      endDate: endOfDay(prevEnd)
    };
  };

  const formatRangeParams = (range) => ({
    startDate: range.startDate.toISOString(),
    endDate: range.endDate.toISOString()
  });

  const formatChange = (current, previous) => {
    if (!previous) return null;
    const diff = current - previous;
    const percent = (diff / previous) * 100;
    return {
      diff,
      percent
    };
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const range = period === 'custom' && appliedRange.startDate && appliedRange.endDate
        ? {
          startDate: new Date(appliedRange.startDate),
          endDate: new Date(appliedRange.endDate),
          label: `${new Date(appliedRange.startDate).toLocaleDateString()} - ${new Date(appliedRange.endDate).toLocaleDateString()}`
        }
        : getRangeForPeriod(period);
      const previousRange = getPreviousRange(range.startDate, range.endDate);

      setPeriodLabel(range.label);

      const [
        usersRes,
        productsRes,
        newUsersRes,
        newProductsRes,
        stockRes,
        debtsRes,
        expensesRes,
        salesRes,
        prevStockRes,
        prevDebtsRes,
        prevExpensesRes,
        prevSalesRes
      ] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/products'),
        axios.get('/api/users', { params: formatRangeParams(range) }),
        axios.get('/api/products', { params: formatRangeParams(range) }),
        axios.get('/api/stock/summary', { params: formatRangeParams(range) }),
        axios.get('/api/debts/summary', { params: formatRangeParams(range) }),
        axios.get('/api/expenses/summary', { params: formatRangeParams(range) }),
        axios.get('/api/transactions/summary', { params: formatRangeParams(range) }),
        axios.get('/api/stock/summary', { params: formatRangeParams(previousRange) }),
        axios.get('/api/debts/summary', { params: formatRangeParams(previousRange) }),
        axios.get('/api/expenses/summary', { params: formatRangeParams(previousRange) }),
        axios.get('/api/transactions/summary', { params: formatRangeParams(previousRange) })
      ]);

      setStats({
        totalUsers: usersRes.data.total || 0,
        totalProducts: productsRes.data.total || 0,
        newUsers: newUsersRes.data.total || 0,
        newProducts: newProductsRes.data.total || 0,
        totalStock: stockRes.data.totalDepotValue || 0,
        totalDebts: debtsRes.data.totalOutstanding || 0,
        totalExpenses: expensesRes.data.totalExpenses || 0,
        totalSales: salesRes.data.totalSales || 0,
        prevTotalStock: prevStockRes.data.totalDepotValue || 0,
        prevTotalDebts: prevDebtsRes.data.totalOutstanding || 0,
        prevTotalExpenses: prevExpensesRes.data.totalExpenses || 0,
        prevTotalSales: prevSalesRes.data.totalSales || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      setActivitiesLoading(true);
      const response = await axios.get('/api/transactions', {
        params: {
          limit: 10,
          page: 1
        }
      });
      setRecentActivities(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'stock_transfer':
      case 'stock_addition':
        return ChartBarIcon;
      case 'sale':
      case 'payment':
        return CurrencyDollarIcon;
      case 'expense':
        return DocumentTextIcon;
      case 'return':
      case 'agent_return':
        return ExclamationTriangleIcon;
      default:
        return DocumentTextIcon;
    }
  };

  const getActivityColor = (type) => {
    switch(type) {
      case 'stock_transfer':
      case 'stock_addition':
        return 'bg-blue-100 text-blue-600';
      case 'sale':
      case 'payment':
        return 'bg-success-100 text-success-600';
      case 'expense':
        return 'bg-red-100 text-red-600';
      case 'return':
      case 'agent_return':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-secondary-100 text-secondary-600';
    }
  };

  const quickActions = [
    {
      name: 'Add User',
      href: '/admin/users',
      icon: UserGroupIcon,
      description: 'Create new user accounts',
      color: 'bg-blue-500'
    },
    {
      name: 'Add Product',
      href: '/admin/products',
      icon: CubeIcon,
      description: 'Add new milk products',
      color: 'bg-green-500'
    },
    {
      name: 'Add Stock',
      href: '/admin/stock',
      icon: ChartBarIcon,
      description: 'Add stock to depot',
      color: 'bg-purple-500'
    },
    {
      name: 'View Reports',
      href: '/admin/reports',
      icon: DocumentTextIcon,
      description: 'Generate business reports',
      color: 'bg-orange-500'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 rounded-xl border-2 border-primary-200 bg-gradient-to-r from-white via-secondary-50 to-primary-50 px-6 py-5 shadow-lg sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-700 to-primary-600 bg-clip-text text-transparent">Admin Dashboard</h1>
          <p className="mt-2 text-sm font-medium text-secondary-700">
            Manage users, products, and monitor business performance.
          </p>
          <p className="mt-1 text-sm font-semibold text-primary-600">Insights: {periodLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className={`btn-secondary ${period === 'monthly' ? 'bg-primary-100 text-primary-700' : ''}`}
            onClick={() => setPeriod('monthly')}
          >
            Monthly
          </button>
          <button
            className={`btn-secondary ${period === 'quarterly' ? 'bg-primary-100 text-primary-700' : ''}`}
            onClick={() => setPeriod('quarterly')}
          >
            Quarterly
          </button>
          <button
            className={`btn-secondary ${period === 'yearly' ? 'bg-primary-100 text-primary-700' : ''}`}
            onClick={() => setPeriod('yearly')}
          >
            Yearly
          </button>
          <button
            className={`btn-secondary ${period === 'custom' ? 'bg-primary-100 text-primary-700' : ''}`}
            onClick={() => setPeriod('custom')}
          >
            Custom
          </button>
        </div>
      </div>

      {period === 'custom' && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div>
            <label className="block text-sm font-medium text-secondary-700">Start date</label>
            <input
              type="date"
              className="input-field"
              value={customRange.startDate}
              onChange={(event) =>
                setCustomRange((prev) => ({ ...prev, startDate: event.target.value }))
              }
              max={customRange.endDate || toInputDate(new Date())}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700">End date</label>
            <input
              type="date"
              className="input-field"
              value={customRange.endDate}
              onChange={(event) =>
                setCustomRange((prev) => ({ ...prev, endDate: event.target.value }))
              }
              min={customRange.startDate || ''}
              max={toInputDate(new Date())}
            />
          </div>
          <button
            className="btn-primary"
            onClick={() =>
              setAppliedRange({
                startDate: customRange.startDate,
                endDate: customRange.endDate
              })
            }
            disabled={!customRange.startDate || !customRange.endDate}
          >
            Apply Range
          </button>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="stat-card-accent p-6">
          <div className="flex items-center justify-between">
            <div>
              <dt className="text-sm font-medium text-white/90 truncate mb-2">
                Total Users
              </dt>
              <dd className="text-3xl font-bold text-white">
                {stats.totalUsers}
              </dd>
              <dd className="text-sm text-white/80 mt-1">
                New in period: {stats.newUsers}
              </dd>
            </div>
            <div className="flex-shrink-0 bg-white/20 p-4 rounded-full">
              <UserGroupIcon className="h-10 w-10 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card-success p-6">
          <div className="flex items-center justify-between">
            <div>
              <dt className="text-sm font-medium text-white/90 truncate mb-2">
                Total Products
              </dt>
              <dd className="text-3xl font-bold text-white">
                {stats.totalProducts}
              </dd>
              <dd className="text-sm text-white/80 mt-1">
                New in period: {stats.newProducts}
              </dd>
            </div>
            <div className="flex-shrink-0 bg-white/20 p-4 rounded-full">
              <CubeIcon className="h-10 w-10 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card-secondary p-6">
          <div className="flex items-center justify-between">
            <div>
              <dt className="text-sm font-semibold text-secondary-900/90 truncate mb-2">
                Stock Value
              </dt>
              <dd className="text-3xl font-bold text-secondary-900">
                {formatCurrency(stats.totalStock)}
              </dd>
              {formatChange(stats.totalStock, stats.prevTotalStock) && (
                <dd className="text-sm text-secondary-900/80 mt-1">
                  vs prev: {formatChange(stats.totalStock, stats.prevTotalStock).percent.toFixed(1)}%
                </dd>
              )}
            </div>
            <div className="flex-shrink-0 bg-white/40 p-4 rounded-full">
              <ChartBarIcon className="h-10 w-10 text-secondary-900" />
            </div>
          </div>
        </div>

        <div className="stat-card-warning p-6">
          <div className="flex items-center justify-between">
            <div>
              <dt className="text-sm font-medium text-white/90 truncate mb-2">
                Outstanding Debt
              </dt>
              <dd className="text-3xl font-bold text-white">
                {formatCurrency(stats.totalDebts)}
              </dd>
              {formatChange(stats.totalDebts, stats.prevTotalDebts) && (
                <dd className="text-sm text-white/80 mt-1">
                  vs prev: {formatChange(stats.totalDebts, stats.prevTotalDebts).percent.toFixed(1)}%
                </dd>
              )}
            </div>
            <div className="flex-shrink-0 bg-white/20 p-4 rounded-full">
              <ExclamationTriangleIcon className="h-10 w-10 text-white" />
            </div>
          </div>
        </div>

        <div className="stat-card-primary p-6">
          <div className="flex items-center justify-between">
            <div>
              <dt className="text-sm font-medium text-white/90 truncate mb-2">
                Total Sales
              </dt>
              <dd className="text-3xl font-bold text-white">
                {formatCurrency(stats.totalSales)}
              </dd>
              {formatChange(stats.totalSales, stats.prevTotalSales) && (
                <dd className="text-sm text-white/80 mt-1">
                  vs prev: {formatChange(stats.totalSales, stats.prevTotalSales).percent.toFixed(1)}%
                </dd>
              )}
            </div>
            <div className="flex-shrink-0 bg-white/20 p-4 rounded-full">
              <CurrencyDollarIcon className="h-10 w-10 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 p-6">
          <div className="flex items-center justify-between">
            <div>
              <dt className="text-sm font-medium text-white/90 truncate mb-2">
                Total Expenses
              </dt>
              <dd className="text-3xl font-bold text-white">
                {formatCurrency(stats.totalExpenses)}
              </dd>
              {formatChange(stats.totalExpenses, stats.prevTotalExpenses) && (
                <dd className="text-sm text-white/80 mt-1">
                  vs prev: {formatChange(stats.totalExpenses, stats.prevTotalExpenses).percent.toFixed(1)}%
                </dd>
              )}
            </div>
            <div className="flex-shrink-0 bg-white/20 p-4 rounded-full">
              <DocumentTextIcon className="h-10 w-10 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 mb-5 bg-gradient-to-r from-primary-700 to-primary-600 bg-clip-text text-transparent">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              className="relative group bg-gradient-to-br from-white to-secondary-50 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-xl border-2 border-secondary-200 hover:border-primary-400 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div>
                <span className={`rounded-xl inline-flex p-4 ${action.color} text-white shadow-lg`}>
                  <action.icon className="h-7 w-7" />
                </span>
              </div>
              <div className="mt-5">
                <h3 className="text-lg font-bold text-secondary-900">
                  {action.name}
                </h3>
                <p className="mt-2 text-sm font-medium text-secondary-600">
                  {action.description}
                </p>
              </div>
              <span
                className="pointer-events-none absolute top-6 right-6 text-secondary-300 group-hover:text-primary-500 transition-colors"
                aria-hidden="true"
              >
                <PlusIcon className="h-6 w-6" />
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activities */}
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 mb-5 bg-gradient-to-r from-primary-700 to-primary-600 bg-clip-text text-transparent">Recent Activities</h2>
        <div className="card bg-gradient-to-br from-white to-secondary-50">
          <div className="card-body">
            {activitiesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : recentActivities.length === 0 ? (
              <p className="text-sm text-secondary-500 text-center py-8">No recent activities yet.</p>
            ) : (
              <div className="flow-root">
                <ul className="-mb-8">
                  {recentActivities.map((activity, activityIdx) => {
                    const ActivityIcon = getActivityIcon(activity.type);
                    return (
                      <li key={activity._id}>
                        <div className="relative pb-8">
                          {activityIdx !== recentActivities.length - 1 ? (
                            <span
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-secondary-200"
                              aria-hidden="true"
                            />
                          ) : null}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-4 ring-white ${getActivityColor(activity.type)}`}>
                                <ActivityIcon className="h-5 w-5" />
                              </span>
                            </div>
                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1">
                              <div>
                                <p className="text-sm font-medium text-secondary-900">
                                  {activity.description}
                                </p>
                                <p className="text-xs text-secondary-500 mt-1">
                                  {activity.product?.name && `${activity.product.name} (${activity.product.size})`}
                                  {activity.quantity && ` - Qty: ${activity.quantity}`}
                                </p>
                                {activity.agent && (
                                  <p className="text-xs text-secondary-500">
                                    Agent: {activity.agent.name}
                                  </p>
                                )}
                              </div>
                              <div className="whitespace-nowrap text-right text-sm text-secondary-500">
                                <div className="font-medium text-secondary-700">
                                  {formatCurrency(activity.amount)}
                                </div>
                                <div className="text-xs">
                                  {new Date(activity.createdAt).toLocaleDateString()}
                                </div>
                                <div className="text-xs">
                                  {new Date(activity.createdAt).toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          <div className="card-body border-t border-secondary-200">
            <Link
              to="/admin/transactions"
              className="text-sm font-semibold text-primary-600 hover:text-primary-700 flex items-center justify-center"
            >
              View all transactions →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
